import { useEffect, useRef, useState } from "react";
import { Calendar, UserPlus, Star, X, BadgeCheck, Quote } from "lucide-react";
import { api } from "../lib/api";
import { Brand } from "../context/CompanyContext";

const ROTATE_MS = 6000;
const STORAGE_DISMISS_KEY = "luxstay-social-proof-dismissed";

/**
 * Silent social-proof toast pinned bottom-left on the public home page.
 * Rotates anonymized recent activity (new bookings / signups / 5★) every
 * 6s with a soft slide-up entrance. Dismissible with localStorage memory
 * so a single click silences it for the rest of the session.
 */
export default function SocialProof() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    try {
      if (sessionStorage.getItem(STORAGE_DISMISS_KEY) === "1") setDismissed(true);
    } catch { /* noop */ }
    api.get("/public/social-proof").then(({ data }) => {
      if (!alive) return;
      setItems(data?.items || []);
    }).catch(() => { /* silent — banner just won't show */ });
    return () => { alive = false; };
  }, []);

  // Rotation timer; small enter delay so it doesn't pop right on splash
  useEffect(() => {
    if (dismissed || items.length === 0) return;
    const enter = setTimeout(() => setVisible(true), 1500);
    timerRef.current = setInterval(() => {
      setVisible(false); // trigger exit animation
      setTimeout(() => {
        setIdx(i => (i + 1) % items.length);
        setVisible(true);
      }, 250);
    }, ROTATE_MS);
    return () => {
      clearTimeout(enter);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [dismissed, items.length]);

  function dismiss() {
    setVisible(false);
    setDismissed(true);
    try { sessionStorage.setItem(STORAGE_DISMISS_KEY, "1"); } catch { /* noop */ }
  }

  if (dismissed || items.length === 0) return null;
  const item = items[idx];
  if (!item) return null;

  return (
    <div
      className={`hidden sm:block fixed bottom-4 left-4 z-30 max-w-xs transition-all duration-300 ${
        visible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
      }`}
      data-testid="social-proof"
      aria-live="polite"
    >
      <div className="lux-card bg-[#041f1b]/92 backdrop-blur-md border border-[#1a4640] shadow-xl flex items-start gap-3 pl-3 pr-2 py-2.5 relative">
        <span
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#041f1b] border border-[#1a4640] flex items-center justify-center shadow"
          title="Atividade verificada"
          data-testid="social-proof-verified"
        >
          <BadgeCheck size={12} className="text-[#d4af37]" strokeWidth={2.5} />
        </span>
        <Bullet type={item.type} stars={item.stars} />
        <div className="min-w-0 flex-1">
          <div className="text-[12px] text-[#fdfbf7] leading-snug">
            <Verb item={item} />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] mt-1 flex items-center gap-2 flex-wrap">
            <span>{item.ago}</span>
          </div>
        </div>
        <button onClick={dismiss}
                className="p-1 -mr-1 -mt-1 text-[#a0b2af] hover:text-[#d4af37] transition-colors"
                aria-label="Fechar notificação"
                data-testid="social-proof-dismiss">
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

function Bullet({ type, stars = 5 }) {
  const cls = "w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5";
  if (type === "rating") {
    return (
      <span className={`${cls} bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30`}>
        <Star size={12} fill="#d4af37" stroke="#d4af37" />
      </span>
    );
  }
  if (type === "signup") {
    return (
      <span className={`${cls} bg-[#10b981]/15 text-[#10b981] border border-[#10b981]/30`}>
        <UserPlus size={12} />
      </span>
    );
  }
  if (type === "testimonial") {
    return (
      <span className={`${cls} bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30`}>
        <Quote size={12} />
      </span>
    );
  }
  return (
    <span className={`${cls} bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30`}>
      <Calendar size={12} />
    </span>
  );
}

function Verb({ item }) {
  const name = <span className="font-semibold text-[#d4af37]">{item.name}</span>;
  const where = item.city ? (
    <> de <span className="text-[#fdfbf7]">{item.city}</span></>
  ) : null;
  if (item.type === "booking") return <>{name}{where} marcou uma limpeza</>;
  if (item.type === "signup") return <>{name}{where} juntou-se à <Brand short /></>;
  if (item.type === "rating") {
    const s = item.stars || 5;
    return <>{name} avaliou com {s}★ a sua última limpeza</>;
  }
  if (item.type === "testimonial") {
    return (
      <>
        <span className="italic text-[#fdfbf7]">«{item.quote}»</span>
        <span className="block text-[10px] uppercase tracking-widest text-[#a0b2af] mt-0.5">
          — {name}{where}
        </span>
      </>
    );
  }
  return null;
}
